import React from 'react';
import PropTypes from 'prop-types';
import MUIDataTable from 'mui-datatables';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';

import { CustomTooltip, Loader } from 'ui';
import { ESGImpactIcon } from 'modules/ESG';
import { getHexColorByImpact } from 'shared/functions';
import { useReportPage } from 'shared/hooks';
import { parseScoreToString } from 'shared/parsers/esg';
import { ESGImpactHexBorderMap } from 'shared/constants/ESGImpactHexCodesMap';

import { Legend } from './Legend/Legend';

const useStyles = makeStyles(() => ({
  root: {
    position: 'relative',
  },
  name: {
    fontSize: useReportPage ? 10 : 14,
  },
  tooltipScore: {
    textTransform: 'capitalize',
  },
}));

const chipStyles = makeStyles(() => ({
  root: {
    width: 100,
    fontWeight: 600,
    height: useReportPage ? 32 : 42,
    borderRadius: 10,
  },
  icon: {
    marginLeft: 'auto',
    marginRight: 'auto',
    transform: useReportPage ? 'scale(0.7)' : 'none',
  },
}));

const getMuiTheme = () => ({
  component: {
    MUIDataTableHeadCell: {
      styleOverrides: {
        fixedHeaderCommon: {
          backgroundColor: '#F5F5F5',
          padding: `${useReportPage ? '6px' : '24px'} 16px`,
          borderBottom: 0,
          fontSize: useReportPage ? 10 : 14,
        },
        fixedHeader: {
          zIndex: 1,
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: `${useReportPage ? '6px' : '16px'} 16px`,
        }
      }
    },
    MUIDataTableSelectCell: {
      styleOverrides: {
        headerCell: {
          zIndex: 1,
        },
        fixedLeft: {
          zIndex: 1,
        }
      }
    }
  },
});

const ESGImpactTable = ({ loading, data, type }) => {
  const classes = useStyles();
  const chipClasses = chipStyles();
  const isIndustry = type === 'industry';

  const options = {
    pagination: false,
    selectableRows: 'none',
    filter: false,
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    elevation: 0,
    responsive: 'standard',
  };

  const chartColumnsOptions = {
    sort: false,
    // eslint-disable-next-line react/prop-types
    customBodyRender: ({ name, impactNumber, impactWeight }) => (
      <CustomTooltip
        placement="top"
        title={
          <>
            <Typography color="primary">{name}</Typography>
            <Typography className={classes.tooltipScore}>
              {`Intensity: ${parseScoreToString(impactNumber)}`}
            </Typography>
            <Typography className={classes.tooltipScore}>
              {`Weight: ${impactWeight || 'none'}`}
            </Typography>
          </>
        }
        arrow
      >
        <Chip
          icon={<ESGImpactIcon impactWeight={impactWeight} />}
          classes={chipClasses}
          style={{
            backgroundColor: getHexColorByImpact(impactNumber, impactWeight),
            border: impactWeight ? 'none' : ESGImpactHexBorderMap.insignificant,
            flexDirection: 'column',
          }}
        />
      </CustomTooltip>
    ),
  };

  const columns = [
    {
      name: isIndustry ? 'industry' : 'region',
      label: isIndustry ? 'Industry' : 'Region',
      options: {
        sort: false,
        customBodyRender: v => (
          <Typography className={classes.name} color="primary">
            {v}
          </Typography>
        ),
      },
    },
    { name: 'environment', label: 'Environment', options: chartColumnsOptions },
    { name: 'social', label: 'Social', options: chartColumnsOptions },
    { name: 'governance', label: 'Governance', options: chartColumnsOptions },
    { name: 'total', label: 'Total', options: chartColumnsOptions },
  ];

  return <>
    <div className={classes.root}>
      {loading && <Loader cover />}
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable columns={columns} options={options} data={data} />
        </ThemeProvider>
      </StyledEngineProvider>
    </div>
    <Box p={2}>
      <Legend />
    </Box>
  </>;
};

ESGImpactTable.defaultProps = {
  loading: false,
};

ESGImpactTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.array.isRequired).isRequired,
  loading: PropTypes.bool,
  type: PropTypes.oneOf(['industry', 'region']).isRequired,
};

export default ESGImpactTable;
